/**
 * Season Pass Web Hooks
 *
 * React Query hooks for the user-facing season pass experience:
 * list active passes, fetch personal progress, and claim tier rewards.
 *
 * SeasonPassesApi methods are cast with `as unknown as Promise<...>` because
 * the @devour/client is regenerated at deploy time — these methods don't exist
 * in the local client build yet.
 */

import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import {SeasonPassesApi, Token, SeasonPassResponse, UserSeasonProgressResponse} from "@devour/client";
import getConfig from "@/utils/getConfig";

export function useGetSeasonPasses(fullToken?: Token) {
    /*
     * GET /season-passes is optionalAuthentication (security: []) — anonymous
     * callers receive the full list of passes (they just earn no XP). The query
     * must therefore run with OR without a token; gating it on
     * `enabled: Boolean(fullToken)` left the Season Pass page and every hub tier
     * bar empty for the consumers that call without a token. The query key still
     * includes the token id so cached responses don't bleed across users in the
     * same browser session — User A logging out and User B logging in gets a
     * fresh fetch instead of A's cached list until staleTime expires.
     */
    return useQuery({
        queryKey: ["season-passes", fullToken?.id ?? "unauthenticated"],
        queryFn: () =>
            (new SeasonPassesApi(getConfig(fullToken)).getSeasonPasses() as unknown as Promise<{ seasonPasses: SeasonPassResponse[] }>),
        staleTime: 60000,
        refetchOnWindowFocus: false,
    });
}

export function useGetSeasonPassMyProgress(fullToken: Token | undefined, seasonPassId: string) {
    return useQuery({
        queryKey: ["season-pass-progress", seasonPassId, fullToken?.id],
        queryFn: () =>
            (new SeasonPassesApi(getConfig(fullToken)).getSeasonPassesIdMyProgress({id: seasonPassId}) as unknown as Promise<UserSeasonProgressResponse>),
        staleTime: 30000,
        refetchOnWindowFocus: false,
        enabled: Boolean(fullToken) && Boolean(seasonPassId),
    });
}

export function useClaimSeasonTier(fullToken: Token | undefined) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({seasonPassId, tierNumber}: { seasonPassId: string; tierNumber: number }) =>
            (new SeasonPassesApi(getConfig(fullToken)).createSeasonPassesIdClaimTierTierNumber({
                id: seasonPassId,
                tierNumber,
            }) as unknown as Promise<UserSeasonProgressResponse>),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({queryKey: ["season-pass-progress", variables.seasonPassId]});
        },
    });
}
